// src/pages/AffiliateProgram.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import FinalCTA from '../components/FinalCTA';
import { IoRocketOutline, IoCheckmarkCircleOutline, IoPeopleOutline, IoCheckmarkCircle, IoCalculatorOutline, IoPricetagsOutline, IoNewspaperOutline, IoHelpCircleOutline, IoBarChartOutline, IoGitNetworkOutline, IoShieldCheckmarkOutline } from 'react-icons/io5';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { submitDemoRequest, fetchPricingPlans } from '../services/apiService';


// --- Currency Conversion Helper ---
const CONVERSION_RATE_INR_TO_USD = 83;

const formatCurrency = (price, currency = 'INR') => {
    if (typeof price !== 'number') return price; // For "Custom"

    if (currency === 'INR') {
        const priceInUsd = Math.round((price / 100) / CONVERSION_RATE_INR_TO_USD);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(priceInUsd);
    }
    
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
    }).format(price / 100);
};


const AffiliateProgram = () => {
    // --- State for Commission Calculator ---
    const [salePrice, setSalePrice] = useState(500);
    const [salesPerWeek, setSalesPerWeek] = useState(5);
    const earningsPerSale = salePrice * 0.5;
    const monthlyEarnings = earningsPerSale * salesPerWeek * 4; // 4 weeks for projection

    // --- State for Pricing Preview ---
    const [pricingPlans, setPricingPlans] = useState([]);

    // --- State for Application Form ---
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        phone: "",
        inquiryType: "Partnerships", // Pre-selected
        message: "",
        updates: false
    });
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [defaultCountry, setDefaultCountry] = useState('US');

    useEffect(() => {
        // Fetch user's country for the phone input
        const fetchCountry = async () => {
            try {
                const response = await fetch('https://ipapi.co/country_code/');
                if (!response.ok) throw new Error('Failed to fetch country');
                const countryCode = await response.text();
                if (countryCode && countryCode.length === 2) {
                    setDefaultCountry(countryCode);
                }
            } catch (err) {
                console.warn("Could not fetch user country, defaulting to US.", err);
            }
        };

        // Fetch pricing plans for the preview section
        const loadPricing = async () => {
            try {
                const apiPlans = await fetchPricingPlans();
                 apiPlans.sort((a, b) => {
                    if (a.isFree) return -1;
                    if (b.isFree) return 1;
                    return (a.monthly?.plan_price || 0) - (b.monthly?.plan_price || 0);
                });

                const transformed = apiPlans.map(p => {
                    if (!p.monthly) return null;
                    return {
                        name: p.monthly.plan_name.replace(' - Monthly', ''),
                        description: p.monthly.plan_description,
                        price: p.isCustom ? "Custom" : formatCurrency(p.monthly.plan_price, p.monthly.plan_currency),
                        features: [
                            `${p.monthly.brands_limit} Brand Profiles`,
                            `${p.monthly.posts_per_month_limit} Posts/Month`,
                            `${p.monthly.ai_social_post_credits_limit} AI Credits/mo`,
                        ],
                        isPopular: p.base_plan_name === "Pro"
                    };
                }).filter(Boolean); // remove nulls
                
                setPricingPlans(transformed.slice(0, 3)); // Show first 3 plans
            } catch (pricingError) {
                console.error("Failed to load pricing plans:", pricingError);
            }
        };

        fetchCountry();
        loadPricing();
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const handlePhoneChange = (value) => {
        setFormData(prev => ({ ...prev, phone: value || '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');
        try {
            await submitDemoRequest(formData);
            setStatus('success');
            setFormData({ firstName: "", lastName: "", email: "", company: "", phone: "", inquiryType: "Partnerships", message: "", updates: false });
        } catch (err) {
            setStatus('error');
            setError(err.message || "Something went wrong. Please try again.");
        }
    };


    // --- Static Data for Page Sections ---
    const whyJoinPoints = [
        { text: 'Flat 50% commission on every new sale you bring in' },
        { text: 'Weekly payouts directly to your bank or PayPal' },
        { text: 'No contracts, no hidden terms — just pure commission' },
        { text: 'Work directly with our team via personal communication' },
    ];

    const whoCanJoin = [
        { title: 'Freelancers', description: 'Add a new income stream while serving clients.' },
        { title: 'Micro-influencers', description: 'Monetize your audience with an easy SaaS recommendation.' },
        { title: 'Sales Professionals', description: 'Turn your sales skills into weekly payouts.' },
        { title: 'Tax & CA Professionals', description: 'Recommend Oolook to your business clients.' },
        { title: 'Content Agencies', description: 'Offer a trusted tool to your clients and earn commission.' },
        { title: 'Anyone with a network', description: 'If you can recommend tools, you can earn with Oolook.' },
    ];

    const howItWorks = [
        'Sign up as an affiliate partner → Quick, free, no contract.',
        'Get your unique affiliate link (we’ll provide it directly via email/WhatsApp).',
        'Share it with your audience or clients.',
        'Earn 50% commission every time someone signs up using your link.',
        'Get paid weekly straight to your bank account or PayPal.',
    ];

     const programDifferences = [
        { title: '50% Upfront Commission', description: 'Most SaaS programs offer 10-20% spread over months. We give you a massive 50% upfront, so you earn much faster.' },
        { title: 'Weekly Payouts', description: 'Stop waiting 30-60 days for your money. We process payments every week, ensuring you get your earnings quickly.' },
        { title: 'No Contract Headaches', description: 'We believe in a simple partnership: you bring sales, we pay you. It\'s zero risk and pure reward.' },
        { title: 'Direct Human Contact', description: 'Forget automated emails and complex dashboards. You\'ll work directly with our team via WhatsApp or email for a transparent and personal experience.' },
    ];

    const faqs = [
        { q: 'Do I need to sign a contract?', a: 'No. There are no contracts. You earn, we pay. Simple.' },
        { q: 'How will I get paid?', a: 'We send payouts weekly to your bank account (India) or PayPal (international).' },
        { q: 'How much can I earn?', a: 'There’s no limit. If you bring in 1 customer, you earn 50% of that sale. If you bring in 100, you earn 50% of all those sales.' },
        { q: 'When do I receive my first payout?', a: 'You will receive your payout in the following week after your first successful sale is confirmed. We process all affiliate payments on a weekly basis.' },
        { q: 'Do I earn from recurring payments?', a: 'No. Commission is only on the first payment. This is why the upfront rate is very high at 50%, unlike most SaaS programs (which usually give just 10–20%).' },
        { q: 'What countries can affiliates join from?', a: 'Our program is global! Anyone can join as long as you can receive payments via PayPal (for international partners) or direct bank transfer (for partners in India).' },
        { q: 'How do I track my sales without a dashboard?', a: 'Currently, we’ll share all tracking and confirmations directly with you (via WhatsApp/email). You’ll always know what you’ve earned.' },
        { q: 'Who can join?', a: 'Anyone who has a network: freelancers, influencers, salespeople, tax consultants, or even students who want to earn extra income.' },
        { q: 'Is there a joining fee?', a: 'No. Joining is 100% free.' },
        { q: 'Can I promote Oolook through ads?', a: 'Yes, but ads must be ethical (no misleading claims, no spam). Always use approved messaging provided by us.' },
        { q: 'What kind of sales materials do you provide?', a: 'Once you\'re approved, we\'ll send you a partner kit that includes logos, banners, pre-written copy, and best practices for promoting Oolook effectively. We want to make it as easy as possible for you to succeed.' },
        { q: 'Is there a minimum payout threshold?', a: 'Yes, our minimum payout threshold is a low $30 (or ₹2,000). This ensures we can process payments efficiently every week.' },
        { q: 'Can I track my link clicks?', a: 'While we don\'t have a self-serve dashboard right now, our direct communication model means you can always ask for an update! We provide transparent reports on clicks that convert to sales.' },
        { q: 'What if the customer I refer gets a refund?', a: 'Our commission is paid on successful, non-refunded first payments. In the rare case of a refund, the corresponding commission will be adjusted from your next payout. We have a very low refund rate, so this is typically not an issue.' },
    ];

    return (
        <>
            <SEO
                title="Oolook Affiliate Partner Program | Earn 50% Commission"
                description="Join the Oolook Affiliate Program and earn a flat 50% commission on every sale. Simple, fast, and transparent with weekly payouts. Sign up for free!"
                noIndex={false}
            />
            <main className="bg-dark-bg text-dark-text-secondary font-sans animate-fade-in">
                <div className="relative isolate">
                    {/* Hero Section */}
                    <div className="relative px-6 py-32 text-center sm:py-48 lg:px-8">
                        <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
                            <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" style={{ clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)' }} />
                        </div>
                        <h1 className="text-4xl font-bold tracking-tight text-dark-text-primary sm:text-6xl">Oolook Affiliate Partner Program</h1>
                        <p className="mt-6 mx-auto max-w-2xl text-lg leading-8 text-dark-text-secondary">
                            Share Oolook. Earn 50% Commission. Simple, Fast & Transparent.
                        </p>
                        <div className="mt-10">
                            <a href="#affiliate-form" className="rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-5 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90">
                                Apply Now & Start Earning
                            </a>
                        </div>
                    </div>

                    {/* Why Join Section */}
                    <section className="py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl lg:text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoRocketOutline className="inline-block h-5 w-5 mr-2" /> Why Join Oolook’s Affiliate Program?
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">
                                    A Partnership Built for Creators and Connectors
                                </p>
                                <p className="mt-6 text-lg leading-8 text-dark-text-secondary">
                                    We’re opening the doors for freelancers, consultants, influencers, and anyone with a network to earn massive commissions just by recommending Oolook.
                                </p>
                            </div>
                            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
                                <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
                                    {whyJoinPoints.map((point, index) => (
                                        <div key={index} className="relative pl-9">
                                            <dt className="inline font-semibold text-dark-text-primary">
                                                <IoCheckmarkCircleOutline className="absolute left-1 top-1 h-6 w-6 text-dark-accent-start" />
                                                {point.text}
                                            </dt>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>
                    </section>

                    {/* Commission Calculator Section */}
                    <section className="bg-dark-card/20 py-24 sm:py-32">
                        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
                            <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                <IoCalculatorOutline className="inline-block h-5 w-5 mr-2" /> Commission Calculator
                            </h2>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Visualize Your Earnings</p>
                            <p className="mt-6 text-lg text-dark-text-secondary">Use the calculator below to see your potential earnings from a single sale or over a month.</p>
                            <div className="mt-12 max-w-xl mx-auto rounded-2xl bg-dark-card/40 p-8 border border-white/10 space-y-8">
                                <div>
                                    <div className="flex justify-between items-center text-dark-text-primary font-medium">
                                        <span>Average Sale Price</span>
                                        <span className="text-2xl font-bold">${salePrice.toLocaleString()}</span>
                                    </div>
                                    <input type="range" min="50" max="5000" step="50" value={salePrice} onChange={(e) => setSalePrice(Number(e.target.value))} className="w-full h-2 bg-dark-card rounded-lg appearance-none cursor-pointer my-4" />
                                </div>
                                <div>
                                    <div className="flex justify-between items-center text-dark-text-primary font-medium">
                                        <span>Sales Per Week</span>
                                        <span className="text-2xl font-bold">{salesPerWeek}</span>
                                    </div>
                                    <input type="range" min="1" max="50" step="1" value={salesPerWeek} onChange={(e) => setSalesPerWeek(Number(e.target.value))} className="w-full h-2 bg-dark-card rounded-lg appearance-none cursor-pointer my-4" />
                                </div>
                                <div className="text-left text-sm text-dark-text-secondary">
                                    <p>If you close <span className="font-bold text-white">{salesPerWeek}</span> sales per week at <span className="font-bold text-white">${salePrice.toLocaleString()}</span> each...</p>
                                </div>
                                <hr className="border-white/10" />
                                <div className="flex justify-between items-center text-dark-text-primary font-bold">
                                    <span className="text-lg">Your Potential Monthly Earnings</span>
                                    <span className="text-3xl text-dark-accent-start">${monthlyEarnings.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </section>
                    
                     {/* Pricing Preview Section */}
                     <section className="py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl lg:text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoPricetagsOutline className="inline-block h-5 w-5 mr-2" /> Plans & Pricing
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">
                                    What You'll Be Selling
                                </p>
                                <p className="mt-6 text-lg leading-8 text-dark-text-secondary">
                                    Familiarize yourself with our simple, value-packed plans. These are what your audience will subscribe to.
                                </p>
                            </div>
                            {pricingPlans.length > 0 && (
                                <div className="isolate mx-auto mt-16 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                {pricingPlans.map((plan) => (
                                    <div key={plan.name} className={`flex flex-col rounded-3xl p-8 ring-1 ${plan.isPopular ? 'ring-2 ring-dark-accent-start bg-dark-card' : 'ring-white/10 bg-dark-card/40'}`}>
                                    <h3 className="text-lg font-semibold leading-8 text-dark-text-primary">{plan.name}</h3>
                                    <p className="mt-4 text-sm leading-6 text-dark-text-secondary">{plan.description}</p>
                                    <p className="mt-6 flex items-baseline gap-x-1">
                                        <span className="text-4xl font-bold tracking-tight text-dark-text-primary">{plan.price}</span>
                                        {plan.price !== "Custom" && plan.price !== "$0" && <span className="text-sm font-semibold leading-6 text-dark-text-secondary">/month</span>}
                                    </p>
                                    <ul className="mt-8 space-y-3 text-sm leading-6 text-dark-text-secondary flex-1">
                                        {plan.features.map(feature => (
                                        <li key={feature} className="flex gap-x-3">
                                            <IoCheckmarkCircle className="h-6 w-5 flex-none text-dark-accent-start" />
                                            {feature}
                                        </li>
                                        ))}
                                    </ul>
                                    <Link to="/pricing" className="mt-8 block rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 bg-white/10 text-white hover:bg-white/20">
                                        Learn more
                                    </Link>
                                    </div>
                                ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Who Can Join */}
                    <section className="bg-dark-card/20 py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl lg:text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoPeopleOutline className="inline-block h-5 w-5 mr-2" /> Who Can Join?
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">
                                    Built For Everyone With a Network
                                </p>
                            </div>
                            <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 sm:mt-20 lg:max-w-none md:grid-cols-2 lg:grid-cols-3">
                                {whoCanJoin.map(person => (
                                    <div key={person.title} className="p-8 bg-dark-card/40 backdrop-blur-lg rounded-2xl border border-white/10">
                                        <h3 className="text-lg font-semibold text-dark-text-primary">{person.title}</h3>
                                        <p className="mt-2 text-sm text-dark-text-secondary">{person.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                    
                    {/* Why Oolook Program is Different */}
                    <section className="py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoBarChartOutline className="inline-block h-5 w-5 mr-2" /> Our Advantage
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Why Our Affiliate Program is Different</p>
                                <p className="mt-4 text-lg leading-8 text-dark-text-secondary">You bring the sales, we provide the commission. It's a simple, powerful partnership.</p>
                            </div>
                            <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-8 sm:mt-20 lg:max-w-none md:grid-cols-2">
                                {programDifferences.map((item) => (
                                <div key={item.title} className="flex items-start gap-x-6 p-8 bg-dark-card/40 backdrop-blur-lg rounded-2xl border border-white/10">
                                    <IoCheckmarkCircleOutline className="h-8 w-8 flex-none text-dark-accent-start mt-1" aria-hidden="true" />
                                    <div>
                                        <h3 className="text-lg font-semibold text-dark-text-primary">{item.title}</h3>
                                        <p className="mt-2 text-sm text-dark-text-secondary">{item.description}</p>
                                    </div>
                                </div>
                                ))}
                            </div>
                        </div>
                    </section>
                    
                    {/* How It Works */}
                    <section className="bg-dark-card/20 py-24 sm:py-32">
                        <div className="mx-auto max-w-7xl px-6 lg:px-8">
                            <div className="mx-auto max-w-2xl text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoGitNetworkOutline className="inline-block h-5 w-5 mr-2" /> The Process
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">How It Works</p>
                            </div>
                            <div className="mx-auto mt-16 max-w-2xl">
                                <ol className="space-y-10">
                                    {howItWorks.map((step, index) => (
                                    <li key={index} className="flex gap-x-4">
                                        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-dark-accent-start/20">
                                        <span className="font-bold text-dark-accent-start">{index + 1}</span>
                                        </div>
                                        <p className="text-base leading-7 text-dark-text-secondary">{step}</p>
                                    </li>
                                    ))}
                                </ol>
                            </div>
                        </div>
                    </section>

                    {/* Scarcity Note */}
                    <div className="mx-auto max-w-4xl px-6 lg:px-8 pt-24 text-center">
                        <div className="inline-block p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg text-yellow-300">
                            <p><span className="font-bold">Beta Program Notice:</span> We are only onboarding the first 200 affiliates in this initial phase to ensure personalized support. Apply now to secure your spot!</p>
                        </div>
                    </div>

                    {/* Application Form */}
                    <section id="affiliate-form" className="pt-16 pb-24 sm:pb-32">
                        <div className="mx-auto max-w-2xl text-center">
                            <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                <IoNewspaperOutline className="inline-block h-5 w-5 mr-2" /> Become a Partner
                            </h2>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Ready to Earn?</p>
                            <p className="mt-4 text-lg text-dark-text-secondary">Fill the form below to join the program and receive your unique affiliate link and sales materials.</p>
                        </div>
                        <div className="mx-auto mt-16 max-w-2xl">
                            {status === 'success' ? (
                                <div className="text-center py-16 bg-dark-card/40 rounded-2xl border border-white/10">
                                    <IoCheckmarkCircle className="mx-auto h-12 w-12 text-green-400" />
                                    <h3 className="mt-4 text-lg font-semibold text-dark-text-primary">Thank you!</h3>
                                    <p className="mt-2 text-dark-text-secondary">Your application has been submitted. Our team will be in touch shortly with your sales materials!</p>
                                </div>
                            ) : (
                                <div className="bg-dark-card/40 backdrop-blur-lg border border-white/10 rounded-2xl p-8 lg:p-12">
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                                            <div>
                                                <label htmlFor="firstName" className="block text-sm font-medium text-dark-text-primary">First name</label>
                                                <input type="text" name="firstName" id="firstName" required value={formData.firstName} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                                            </div>
                                            <div>
                                                <label htmlFor="lastName" className="block text-sm font-medium text-dark-text-primary">Last name</label>
                                                <input type="text" name="lastName" id="lastName" required value={formData.lastName} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-dark-text-primary">Work Email</label>
                                            <input type="email" name="email" id="email" required value={formData.email} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                                        </div>
                                        <div>
                                            <label htmlFor="company" className="block text-sm font-medium text-dark-text-primary">Company / Website (Optional)</label>
                                            <input type="text" name="company" id="company" value={formData.company} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                                        </div>
                                        <div>
                                            <label htmlFor="phone" className="block text-sm font-medium text-dark-text-primary">Phone number</label>
                                            <PhoneInput id="phone" international defaultCountry={defaultCountry} value={formData.phone} onChange={handlePhoneChange} className="mt-2" />
                                        </div>
                                        <input type="hidden" name="inquiryType" value="Partnerships" />
                                        <div>
                                            <label htmlFor="message" className="block text-sm font-medium text-dark-text-primary">How do you plan to promote Oolook?</label>
                                            <div className="mt-2">
                                                <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} placeholder="e.g., Through my blog, to my agency clients, on my YouTube channel..." className="block w-full rounded-md border-0 bg-white/5 py-2 px-3 text-dark-text-primary shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start"/>
                                            </div>
                                        </div>
                                        <div>
                                            <button type="submit" disabled={status === 'loading'} className="w-full rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50">
                                                {status === 'loading' ? 'Submitting...' : 'Join the Program'}
                                            </button>
                                        </div>
                                        {status === 'error' && <p className="text-sm text-red-400 text-center">{error}</p>}
                                    </form>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Compliance Note */}
                    <section className="pb-24 sm:pb-32">
                         <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
                            <div className="inline-flex items-center gap-x-3">
                                <IoShieldCheckmarkOutline className="h-6 w-6 text-dark-text-secondary" />
                                <h3 className="text-lg font-semibold text-dark-text-primary">A Note on Fair Play</h3>
                            </div>
                            <p className="mt-4 text-sm text-dark-text-secondary max-w-2xl mx-auto">
                                All affiliates must adhere to ethical promotion guidelines. This means no spamming, no misleading claims, and no impersonating Oolook. We believe in building trust together.
                            </p>
                        </div>
                    </section>

                    {/* FAQ Section */}
                    <section className="bg-dark-card/20 py-24 sm:py-32">
                        <div className="mx-auto max-w-4xl px-6 lg:px-8">
                             <div className="mx-auto max-w-2xl text-center">
                                <h2 className="text-base font-semibold leading-7 bg-gradient-to-r from-dark-accent-start to-dark-accent-end bg-clip-text text-transparent">
                                    <IoHelpCircleOutline className="inline-block h-5 w-5 mr-2" /> Questions?
                                </h2>
                                <p className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Frequently Asked Questions</p>
                            </div>
                            <dl className="mt-16 space-y-8">
                                {faqs.map((faq) => (
                                <div key={faq.q} className="pt-8 border-t border-white/10">
                                    <dt className="text-lg font-semibold text-dark-text-primary">{faq.q}</dt>
                                    <dd className="mt-2 text-base text-dark-text-secondary">{faq.a}</dd>
                                </div>
                                ))}
                            </dl>
                        </div>
                    </section>
                    
                    <FinalCTA />
                </div>
            </main>
        </>
    );
};

export default AffiliateProgram;